import * as yup from "yup"
import { PHONE_REGEX_US, EMAIL_REGEX } from "../../Constants"

export const PeopleSchema = yup.object({
    name: yup.string()
        .transform((v, o) => (typeof o === "string" && o.trim() === "" ? null : v))
        .when('role', {
            is: (role) => role === "VEHICLE_DRIVER",
            then: (schema) => schema
                .nullable()
                .matches(/^[a-zA-Z\s]+$/, { message: "Only letters and spaces allowed", excludeEmptyString: true }),
            otherwise: (schema) => schema
                .min(2, 'Name must be at least 2 characters')
                .matches(/^[a-zA-Z\s]+$/, "Only letters and spaces allowed")
                .required('Name is required'),
        }),
    role: yup.string()
        .required('Role is required'),
    phoneNumber: yup.string()
        .matches(PHONE_REGEX_US, 'Phone number must start with +1 followed by 10 digits')
        .transform((v, o) => (typeof o === "string" && o.trim() === "" ? null : v))
        .nullable(),
    email: yup.string()
        .matches(EMAIL_REGEX, "Invalid email format")
        .transform((v, o) => (typeof o === "string" && o.trim() === "" ? null : v))
        .nullable(),
    address: yup.string()
        .transform((v, o) => (typeof o === "string" && o.trim() === "" ? null : v))
        .nullable(),
    driverLicense: yup.string()
        .nullable(),
    injuries: yup.string()
        .nullable(),
    statement: yup.string()
        .nullable(),
    isBusinessEntity: yup.boolean()
        .nullable(),
    passengerPosition: yup.string()
        .nullable(),
})

export const AdjusterSchema = yup.object({
    name: yup.string().required('Adjuster name is required'),
    phone: yup.string()
        .transform((v, o) => (typeof o === "string" && o.trim() === "" ? null : v))
        .matches(PHONE_REGEX_US, 'Phone number must start with +1 followed by 10 digits')
        .nullable(),
    email: yup.string()
        .transform((v, o) => (typeof o === "string" && o.trim() === "" ? null : v))
        .matches(EMAIL_REGEX, "Invalid email format")
        .nullable(),
    role: yup.string()
        .transform((v, o) => (typeof o === "string" && o.trim() === "" ? null : v))
        .nullable(),
})

export const RepresentativeSchema = yup.object({
    name: yup.string().required('Name is required'),
    businessName: yup.string().required('Business Name is required'),
    phone: yup.string()
        .transform((v, o) => (typeof o === "string" && o.trim() === "" ? null : v))
        .matches(PHONE_REGEX_US, 'Phone number must start with +1 followed by 10 digits')
        .nullable(),
    email: yup.string()
        .transform((v, o) => (typeof o === "string" && o.trim() === "" ? null : v))
        .matches(EMAIL_REGEX, "Invalid email format")
        .nullable(),
    address: yup.string()
        .transform((v, o) => (typeof o === "string" && o.trim() === "" ? null : v))
        .nullable(),
    role: yup.string()
        .transform((v, o) => (typeof o === "string" && o.trim() === "" ? null : v))
        .nullable(),
})


export const VehicleSchema = yup.object({
    make: yup.string()
        .min(2, 'Make must be at least 2 characters')
        .required('Make is required'),
    model: yup.string()
        .nullable()
        .transform((v, o) => (typeof o === "string" && o.trim() === "" ? null : v)),
    year: yup.string()
        .nullable()
        .transform((v, o) => (typeof o === "string" && o.trim() === "" ? null : v))
        .matches(/^(19|20)\d{2}$/, "Enter a valid 4-digit year"),
    vin: yup.string()
        .matches(/^[A-HJ-NPR-Z0-9]{17}$/, "VIN must be exactly 17 characters (excluding I, O, Q)")
        .transform((v, o) => (typeof o === "string" && o.trim() === "" ? null : v))
        .nullable()
        .notRequired(),
    plateNumber: yup.string()
        .nullable(),
    damageDescription: yup.string()
        .nullable(),
    insuranceCompany: yup.string()
        .nullable(),
    policyNumber: yup.string()
        .nullable(),
})

export const IncidentSchema = yup.object({
    reportedBy: yup.string().required("Reported by is required"),
    notificationDatetime: yup.string().nullable(),
    incidentDate: yup.string().nullable(),
    incidentTime: yup.string().nullable(),
    description: yup.string().nullable(),
})


export const LiabilityClaimSchema = yup.object().shape({
    incidentId: yup.string().required('Incident is required'),
    externalId: yup.string().nullable(),
    status: yup.string().required('Status is required'),
    tpaId: yup.string().nullable().required('TPA is required'),
    filedDate: yup.string()
        .nullable()
        .test('not-in-future', 'Filed Date cannot be in the future', value => !value || new Date(value) <= new Date())
        .test(
            'not-before-acknowledgment-date',
            'Filed Date cannot be greater than Acknowledgment Date',
            function (value) {
                const acknowledgmentDate = this?.parent?.acknowledgmentDate
                return !value || !acknowledgmentDate || new Date(value) <= new Date(acknowledgmentDate)
            }
        ),
    acknowledgmentDate: yup.string()
        .nullable()
        .test('not-in-future', 'Acknowledgment Date cannot be in the future', value => !value || new Date(value) <= new Date())
        .test(
            'not-after-filed-date',
            'Acknowledgment Date cannot be less than Filed Date',
            function (value) {
                const filedDate = this?.parent?.filedDate
                return !value || !filedDate || new Date(value) >= new Date(filedDate)
            }
        ),
    reservedAmount: yup.number()
        .nullable()
        .typeError('Reserved Amount must be a number')
        .min(0, 'Reserved Amount cannot be negative'),
    paidAmount: yup.number()
        .nullable()
        .typeError('Paid Amount must be a number')
        .min(0, 'Paid Amount cannot be negative'),
    inLitigation: yup.boolean().nullable(),
    adjusterName: yup.string().nullable(),
    adjusterEmail: yup.string().nullable(),
    notes: yup.string().nullable(),
})

export const SubrogationClaimSchema = yup.object().shape({
    incidentId: yup.string().required('Incident is required'),
    tpaId: yup.string().required('TPA is required'),
    partyId: yup.string().required('Party is required'),
    externalId: yup.string().nullable(),
    status: yup.string().nullable(),
    adjusterId: yup.string().nullable(),
    filedDate: yup.string()
        .nullable()
        .test('not-in-future', 'Filed Date cannot be in the future', value => !value || new Date(value) <= new Date())
        .test(
            'not-before-acknowledgment-date',
            'Filed Date cannot be greater than Acknowledgment Date',
            function (value) {
                const acknowledgmentDate = this?.parent?.acknowledgmentDate
                return !value || !acknowledgmentDate || new Date(value) <= new Date(acknowledgmentDate)
            }
        ),
    acknowledgmentDate: yup.string()
        .nullable()
        .test('not-in-future', 'Acknowledgment Date cannot be in the future', value => !value || new Date(value) <= new Date())
        .test(
            'not-after-filed-date',
            'Acknowledgment Date cannot be less than Filed Date',
            function (value) {
                const filedDate = this?.parent?.filedDate
                return !value || !filedDate || new Date(value) >= new Date(filedDate)
            }
        ),
    paymentId: yup.string().nullable(),
    inLitigation: yup.boolean().nullable(),
    assignedToIds: yup.array().nullable(),
    tpaExpenses: yup.number()
        .nullable()
        .typeError('TPA Expenses must be a number')
        .min(0, 'TPA Expenses cannot be negative'),
    estimatedRepair: yup.number()
        .nullable()
        .typeError('Estimated Repair must be a number')
        .min(0, 'Estimated Repair cannot be negative'),
    estimatedPd: yup.number()
        .nullable()
        .typeError('Estimated Property Damage must be a number')
        .min(0, 'Estimated Property Damage cannot be negative'),
    estimatedLou: yup.number()
        .nullable()
        .typeError('Estimated Loss of Use must be a number')
        .min(0, 'Estimated Loss of Use cannot be negative'),
    estimatedDv: yup.number()
        .nullable()
        .typeError('Estimated Diminished Value must be a number')
        .min(0, 'Estimated Diminished Value cannot be negative'),
    estimatedTs: yup.number()
        .nullable()
        .typeError('Estimated Tow & Storage must be a number')
        .min(0, 'Estimated Tow & Storage cannot be negative'),
    recoveredPd: yup.number()
        .nullable()
        .typeError('Recovered Property Damage must be a number')
        .min(0, 'Recovered Property Damage cannot be negative'),
    recoveredLou: yup.number()
        .nullable()
        .typeError('Recovered Loss of Use must be a number')
        .min(0, 'Recovered Loss of Use cannot be negative'),
    recoveredDv: yup.number()
        .nullable()
        .typeError('Recovered Diminished Value must be a number')
        .min(0, 'Recovered Diminished Value cannot be negative'),
    recoveredTs: yup.number()
        .nullable()
        .typeError('Recovered Tow & Storage must be a number')
        .min(0, 'Recovered Tow & Storage cannot be negative'),
    dailyRate: yup.number()
        .nullable()
        .typeError('Daily Rate must be a number')
        .min(0, 'Daily Rate cannot be negative'),
    daysDown: yup.number()
        .nullable()
        .typeError('Days Down must be a number')
        .min(0, 'Days Down cannot be negative'),
    notes: yup.string().nullable(),
})
